import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useMemo } from 'react';
import Script from 'next/script';
import { getStructuredData } from '../../../data/structured';
import useNextConfig from '../../hooks/useNextConfig/useNextConfig';
import { requireFromStaticAsset } from '../../utils/assetsUtils';
import { relativeToAbsoluteURL } from '../../utils/links';

const SOCIAL_PREVIEW = 'default-preview-social.png';

export interface PageHeadProps {
  SEO: {
    title: string,
    description: string,
    canonical?: string,
    robots?: string[],
    next?: string,
    prev?: string,
    keywords?: string[],
  },
  type?: string,
  article?: {
    author?: string,
    section?: string,
    tags?: string[],
  },
  image?: {
    url: string,
    width?: number,
    height?: number,
  },
  structuredData?: any,
}

const PageHead: FunctionComponent<PageHeadProps> = ({
  SEO,
  type,
  image,
  article,
  structuredData,
}) => {
  const { asPath } = useRouter();
  const { GTM } = useNextConfig();
  const pageURL = useMemo(
    () => SEO.canonical || relativeToAbsoluteURL(asPath),
    [SEO.canonical, asPath],
  );

  return (
    <>
      <Head>
        <title key="title">{`${SEO.title} | Dove.it`}</title>
        <meta key="viewport" name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta key="description" name="description" content={SEO.description} />
        <meta key="theme-color" name="theme-color" content="#E31D93" />
        <meta key="format-detection" name="format-detection" content="telephone=no" />
        <meta key="og:title" property="og:title" content={SEO.title} />
        <meta key="og:description" property="og:description" content={SEO.description} />
        <meta key="og:type" property="og:type" content={type || 'website'} />
        <meta key="og:url" property="og:url" content={pageURL} />
        <meta key="og:image" property="og:image" content={image && image.url ? image.url : `${requireFromStaticAsset(SOCIAL_PREVIEW)}`} />
        <meta key="twitter:title" property="twitter:title" content={SEO.title} />
        <meta key="twitter:description" property="twitter:description" content={SEO.description} />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        {image && image.width && (
        <meta key="og:image:width" property="og:image:width" content={`${image.width}`} />
        )}
        {image && image.height && (
        <meta key="og:image:height" property="og:image:height" content={`${image.height}`} />
        )}
        {SEO.robots && SEO.robots.length > 0 && (
        <meta key="robots" name="robots" content={SEO.robots.join(',')} />
        )}
        {SEO.keywords && SEO.keywords.length > 0 && (
        <meta key="keywords" name="keywords" content={SEO.keywords.join(',')} />
        )}
        {article && (
        <meta key="robots:max-image-preview" name="robots" content="max-image-preview:large" />
        )}
        {article && article.author && (
        <meta key="article:author" property="article:author" content={article.author} />
        )}
        {article && article.section && (
        <meta key="article:section" property="article:section" content={article.section} />
        )}
        {article && article.tags && (
          article.tags.map((tag) => (<meta key={`article:tag:${tag}`} property="article:tag" content={tag} />))
        )}
        <link key="canonical-url" href={pageURL} rel="canonical" />
        {SEO.next && (
        <link key="next" href={SEO.next} rel="next" />
        )}
        {SEO.prev && (
        <link key="prev" href={SEO.prev} rel="prev" />
        )}
        <script
          key="structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData || getStructuredData()) }}
        />
        {/** mutually exclusive, fallback for browsers that don't support preload */}
        <style
          key="async-hide"
          dangerouslySetInnerHTML={{
            __html: '.async-hide { opacity: 0 !important}',
          }}
        />
        <script
          key="vwoSmartCode"
          dangerouslySetInnerHTML={{
            __html: `window._vwo_code || (function() {
                var account_id=888547,
                version=2.1,
                settings_tolerance=2000,
                hide_element='body',
                hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                /* DO NOT EDIT BELOW THIS LINE */
                f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){w._vwo_code.addScript({text:o.responseText})}else{w._vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}w._vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();`,
          }}
        />
      </Head>
      <Script
        key="GTM"
        id="GTM"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM.containerId}');
          `,
        }}
      />
    </>
  );
};

export default PageHead;
